import { useState } from "react";
import { useLocaisEstoque } from "@/features/estoque/hooks/useLocalEstoque";
import type { LocalEstoque, SalvarLocalEstoqueRequest } from "@/features/estoque/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const EMPTY_FORM: SalvarLocalEstoqueRequest = {
  descricao:             "",
  referencia:            "",
  estoqueLocal:          false,
  estoqueDeTerceiros:    false,
  estoquePoderTerceiros: false,
  isLocalPadrao:         false,
  isInativo:             false,
  centroCustoId:         undefined,
};

export default function LocalEstoquePage() {
  const { query, criar, atualizar, excluir } = useLocaisEstoque();

  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [excluirId,     setExcluirId]     = useState<number | null>(null);
  const [editingItem,   setEditingItem]   = useState<LocalEstoque | null>(null);
  const [form,          setForm]          = useState<SalvarLocalEstoqueRequest>(EMPTY_FORM);
  const [busca,         setBusca]         = useState("");

  const itens = query.data?.itens ?? [];
  const itensFiltrados = busca
    ? itens.filter(i =>
        i.descricao.toLowerCase().includes(busca.toLowerCase()) ||
        (i.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : itens;

  const paginacao = usePaginacaoLocal(itensFiltrados);

  const openDialog = (item?: LocalEstoque) => {
    if (item) {
      setEditingItem(item);
      setForm({
        descricao:             item.descricao,
        referencia:            item.referencia ?? "",
        estoqueLocal:          item.estoqueLocal,
        estoqueDeTerceiros:    item.estoqueDeTerceiros,
        estoquePoderTerceiros: item.estoquePoderTerceiros,
        isLocalPadrao:         item.isLocalPadrao,
        isInativo:             item.isInativo,
        centroCustoId:         item.centroCustoId,
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) {
      toast.error("Descrição é obrigatória.");
      return;
    }
    try {
      if (editingItem) {
        await atualizar.mutateAsync({ id: editingItem.id, req: form });
        toast.success("Local de estoque atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Local de estoque criado.");
      }
      setDialogOpen(false);
    } catch {
      // global error toast handled by QueryClient
    }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Local de estoque excluído.");
    } catch {
      // global error toast
    } finally {
      setExcluirId(null);
    }
  };

  const setField = <K extends keyof SalvarLocalEstoqueRequest>(
    key: K,
    value: SalvarLocalEstoqueRequest[K]
  ) => setForm(f => ({ ...f, [key]: value }));

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Locais de Estoque</h1>
          <p className="text-muted-foreground">Gerencie os locais de armazenamento (ex.: Loja, Depósito)</p>
        </div>
        <Button onClick={() => openDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Local
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">Estoque Local</TableHead>
              <TableHead className="text-center">De Terceiros</TableHead>
              <TableHead className="text-center">Em Poder de Terceiros</TableHead>
              <TableHead className="text-center">Padrão</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map(item => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.descricao}</TableCell>
                <TableCell>{item.referencia || "—"}</TableCell>
                <TableCell className="text-center">{item.estoqueLocal          ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{item.estoqueDeTerceiros    ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{item.estoquePoderTerceiros ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">{item.isLocalPadrao         ? "✓" : "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={item.isInativo ? "secondary" : "success"}>
                    {item.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(item.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum local encontrado para esta busca." : "Nenhum local de estoque cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Editar Local de Estoque" : "Novo Local de Estoque"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Descrição + Referência */}
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input
                  id="descricao"
                  maxLength={60}
                  value={form.descricao}
                  onChange={e => setField("descricao", e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="referencia">Referência</Label>
                <Input
                  id="referencia"
                  maxLength={10}
                  className="w-28"
                  value={form.referencia ?? ""}
                  onChange={e => setField("referencia", e.target.value)}
                />
              </div>
            </div>

            {/* Checkboxes de tipo */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Tipo de estoque</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="estoqueLocal"
                    checked={form.estoqueLocal}
                    onCheckedChange={v => setField("estoqueLocal", v)}
                  />
                  <Label htmlFor="estoqueLocal">Estoque local</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="isLocalPadrao"
                    checked={form.isLocalPadrao}
                    onCheckedChange={v => {
                      setField("isLocalPadrao", v);
                      if (v) {
                        const atualPadrao = itens.find(
                          i => i.isLocalPadrao && i.id !== editingItem?.id
                        );
                        if (atualPadrao) {
                          toast.warning(
                            `"${atualPadrao.descricao}" deixará de ser o padrão.`
                          );
                        }
                      }
                    }}
                  />
                  <Label htmlFor="isLocalPadrao">Estoque padrão</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="estoqueDeTerceiros"
                    checked={form.estoqueDeTerceiros}
                    onCheckedChange={v => setField("estoqueDeTerceiros", v)}
                  />
                  <Label htmlFor="estoqueDeTerceiros">De terceiros em poder local</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="estoquePoderTerceiros"
                    checked={form.estoquePoderTerceiros}
                    onCheckedChange={v => setField("estoquePoderTerceiros", v)}
                  />
                  <Label htmlFor="estoquePoderTerceiros">Em poder de terceiros</Label>
                </div>
              </div>
            </div>

            {/* Inativo */}
            <div className="flex items-center gap-2">
              <Switch
                id="isInativo"
                checked={form.isInativo}
                onCheckedChange={v => setField("isInativo", v)}
              />
              <Label htmlFor="isInativo">Inativo</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AlertDialog excluir */}
      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir local de estoque</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. Deseja realmente excluir este local de estoque?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
